import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MyAccountService } from '../pages/my-account/services/my-acount.service';
import { UserProfile } from '../pages/my-account/interfaces/company-settings.interface';
import { LocalStorageKeys } from '../core/enums/LocalStorageKeys';
import { LocalStorageService } from './local-storage.service';
import { filter, firstValueFrom } from 'rxjs';
import { Event, NavigationEnd, Router } from '@angular/router';
import { normalizeLanguageCode } from '../core/utils/languages.util';
@Injectable({
  providedIn: 'root'
})
export class LenguageSystemService implements OnDestroy {
  private hasInitialized = false;
  private languageChangeListener?: (ev: Event) => void;
  private storageListener?: (ev: StorageEvent) => void;

  constructor(
    private translate: TranslateService,
    private MyAccountService: MyAccountService,
    private router: Router,
    private LocalStorageService: LocalStorageService
  ) {
    this.setupLanguageChangeListener();
    this.setupStorageSyncListener();
  }

  initializeLanguageSystemOnce() {
    if (this.hasInitialized) return;

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(() => !this.hasInitialized)
      )
      .subscribe(() => {
        this.hasInitialized = true;
        this.setLanguageSystem();
      });
  }
  async setLanguageSystem() {
    // If user already selected a language (stored locally), use it.
    const savedLanguage = this.LocalStorageService.get<string>(LocalStorageKeys.Language);
    if (savedLanguage) {
      return this.applyLanguage(savedLanguage);
    }

    // If not in local storage, check backend user preference.
    const user = await this.getUserProfile();
    if (user?.UserLanguage) {
      return this.applyLanguage(normalizeLanguageCode(user.UserLanguage));
    }

    // Fallback to browser language.
    const browserLang = normalizeLanguageCode(navigator.language || 'en');
    this.applyLanguage(browserLang);
  }

  private async getUserProfile(): Promise<UserProfile | null> {
    let user = this.MyAccountService.getUserDetailLocal();

    if (!user) {
      try {
        user = await firstValueFrom(this.MyAccountService.getUserDetail());
        if (user) {
          this.MyAccountService.setUserDetailLocal(user);
        }
      } catch (error) {
        console.warn('Error fetching user profile:', error);
      }
    }

    return user || null;
  }

  private applyLanguage(language: string): void {
    this.translate.setDefaultLang(language);
    this.translate.use(language);
    this.LocalStorageService.set(LocalStorageKeys.Language, language, true);
  }

  private setupLanguageChangeListener(): void {
    this.languageChangeListener = () => {
      const browserLang = normalizeLanguageCode(navigator.language || 'en');
      this.applyLanguage(browserLang);
    };
    window.addEventListener('languagechange', this.languageChangeListener as unknown as EventListener);
  }

  private setupStorageSyncListener(): void {
    this.storageListener = (event: StorageEvent) => {
      if (event.key === LocalStorageKeys.Language && event.newValue) {
        this.translate.use(event.newValue);
      }
    };
    window.addEventListener('storage', this.storageListener as unknown as EventListener);
  }

  ngOnDestroy(): void {
    if (this.languageChangeListener) {
      window.removeEventListener('languagechange', this.languageChangeListener as unknown as EventListener);
    }
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener as unknown as EventListener);
    }
  }
}
